import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import BackButton from "../components/BackButton";
import BackToTop from "../components/BackToTop";
import hljs from "../vendor/js/hljs-setup";
import { useEffect } from "react";


export default function ResendRailsSidekiq() {
    useEffect(() => {
        hljs.highlightAll();
    }, []);

    const nameExt = " | charlie-gp";
    const [postTitle, postDate, readTime] = [
        "Resend + Rails: Modern Email Infrastructure",
        "Jan. 30, 2026",
        "4min. read",
    ];

    return (
        <div id="posts">
            <Helmet>
                <title>{postTitle + nameExt}</title>
            </Helmet>
            <BackButton />

            <div id="post-date">
                <small>
                    {postDate} &nbsp;&bull;&nbsp; {readTime} &nbsp;&bull;&nbsp;{" "}
                    <Link to="/">all posts</Link>
                </small>
            </div>

            <h1 id="post-title">{postTitle}</h1>

            <p className="lead">
                Email is a critical communication bridge for mobile apps. For React Native developers, the best approach is offloading email delivery to a robust backend. <strong>Resend</strong> provides a modern developer experience that, when combined with <strong>Rails</strong> and <strong>Sidekiq</strong>, creates a seamless, non-blocking pipeline.
            </p>

            <h2 className="post-sect">Why Resend?</h2>
            <p>
                Resend is built for developers who care about design and deliverability. It skips the complexity of legacy SMTP providers and offers a clean SDK and first-class support for React-based email templates (via React Email).
            </p>

            <h2 className="post-sect">Rails Configuration</h2>
            <h3 className="post-sect-sub">1. Install the SDK</h3>
            <p>Add the gem to your <code>Gemfile</code>:</p>
            <pre>
                <code className="language-ruby">
                    {`gem "resend"`}
                </code>
            </pre>

            <h3 className="post-sect-sub">2. Configure the Initializer</h3>
            <p>Set up your API key in <code>config/initializers/resend.rb</code>:</p>
            <pre>
                <code className="language-ruby">
                    {`Resend.api_key = ENV["RESEND_API_KEY"]`}
                </code>
            </pre>

            <h2 className="post-sect">Sidekiq Integration</h2>
            <p>
                Email delivery can be slow and prone to network timeouts. <strong>Never</strong> send emails synchronously within a React Native API request.
            </p>

            <h3 className="post-sect-sub">The Email Job</h3>
            <pre>
                <code className="language-ruby">
                    {`# app/sidekiq/welcome_email_job.rb
class WelcomeEmailJob
  include Sidekiq::Job

  def perform(user_id)
    user = User.find(user_id)
    
    params = {
      from: "Acme <onboarding@resend.dev>",
      to: [user.email],
      subject: "Welcome to our Mobile App!",
      html: "<strong>Hello #{user.name}!</strong> Thank you for joining."
    }

    Resend::Emails.send(params)
  end
end`}
                </code>
            </pre>

            <h2 className="post-sect">The React Native Flow</h2>
            <p>
                Your React Native app simply hits an endpoint. The Rails controller handles the database logic and hands the email task to Sidekiq.
            </p>
            <pre>
                <code className="language-javascript">
                    {`// React Native - SignUp.js
const handleSignUp = async (data) => {
  const response = await fetch('https://api.yourapp.com/users', {
    method: 'POST',
    body: JSON.stringify(data)
  });
  
  if (response.ok) {
    // The user gets a 'Success' immediately. 
    // The email is being sent in the background by Rails/Sidekiq.
    Alert.alert("Success!", "Check your email to verify your account.");
  }
};`}
                </code>
            </pre>

            <h2 className="post-sect">Final Thoughts</h2>
            <p>
                By combining Resend's deliverability with the background processing power of Sidekiq, you ensure your mobile app remains snappy while maintaining a high-quality communication layer. Focus on the mobile UX, and let the Rails ecosystem handle the heavy lifting.
            </p>

            <BackToTop />

            <div className="mt-5">
                <BackButton />
            </div>
        </div>
    );
}
